// Managed-campaign client consent declaration, v1 (23 Oct 2026).
// No Deno APIs and no network, so Vitest and the browser can import it.
// The wording below is the legal text. Do not paraphrase it.

import { CONSENT_RETENTION_POLICY } from "./clientListConsent.ts";

export const CLIENT_CONSENT_DECLARATION_VERSION = "v1";
export const CLIENT_CONSENT_DECLARATION_DATE = "2026-10-23";
export const CLIENT_CONSENT_DECLARATION_LABEL = "v1, 23 Oct 2026";
export const CLIENT_CONSENT_PDF_BUCKET = "campaign-consent-declarations";
export const CONTACT_LIST_MAX_BYTES = 8_000_000;

export const DECLARATION_TITLE =
  "Speak Managed Campaign – Client Consent Declaration (v1, 23 Oct 2026)";

export const DECLARATION_INTRO =
  "To be completed and signed by the client before each managed campaign is launched, and again whenever a new contact list is uploaded.";

export const DECLARATION_KEYS = [
  "controller",
  "own_list",
  "consent_calls",
  "consent_texts",
  "consent_records",
  "opt_outs",
  "no_special_category",
  "disclosure",
  "accuracy",
] as const;

export type DeclarationKey = (typeof DECLARATION_KEYS)[number];

/** Word for word from the v1 declaration. [Client name] is filled with the client legal name. */
export const DECLARATION_TEMPLATES: ReadonlyArray<{ key: DeclarationKey; text: string }> = [
  {
    key: "controller",
    text: "We are the controller. We are the controller of the contacts' personal data. Vidara, LLC (Speak) processes it only on our instructions, under the data processing terms in the Speak Terms.",
  },
  {
    key: "own_list",
    text: "Our own list. Every contact on this list gave their details to us directly. The list hasn't been bought, rented or obtained from a third party.",
  },
  {
    key: "consent_calls",
    text: "Consent to calls. Every contact has given us specific consent to receive calls from [Client name] for this purpose. For AI voice calls, that consent covers automated or AI-voiced calls.",
  },
  {
    key: "consent_texts",
    text: "Consent to texts. Where SMS is used, every individual contact has consented to texts from us, or is an existing customer under the soft opt-in, and was given a simple way to opt out.",
  },
  {
    key: "consent_records",
    text: "Consent records. We hold a record of each consent: who gave it, when, how, and the wording they saw. We have uploaded those records to Speak, or will provide them within 2 working days of a request.",
  },
  {
    key: "opt_outs",
    text: "Opt-outs removed. We have removed anyone who has withdrawn consent or asked us not to contact them. We understand Speak will also suppress any number on its Speak-wide do-not-contact list.",
  },
  {
    key: "no_special_category",
    text: "No special category data or children. The list contains no special category data and no contacts we know to be under 18, unless agreed in writing with Speak.",
  },
  {
    key: "disclosure",
    text: "Disclosure and recording. We agree each call will open by saying it is an AI assistant calling on our behalf and that the call is recorded. Recordings and transcripts are kept for 30 days unless we agree otherwise in writing.",
  },
  {
    key: "accuracy",
    text: "Accuracy and indemnity. These statements are true. We will tell Speak at once if any becomes untrue. We are responsible, as set out in the Speak Terms, for any claim arising from a contact list or consent we provided.",
  },
];

export const AUTHORISATION_TEMPLATE =
  "I am authorised to sign for [Client] and I agree to the declarations above.";

export const CHANNEL_OPTIONS = [
  { value: "ai_voice", label: "AI voice calls" },
  { value: "sms", label: "SMS" },
  { value: "both", label: "both" },
] as const;

export const CONSENT_SOURCE_OPTIONS = [
  { value: "web_form", label: "web form" },
  { value: "app", label: "app" },
  { value: "in_person", label: "in person" },
  { value: "contract", label: "contract" },
] as const;

export type DeclarationChannel = (typeof CHANNEL_OPTIONS)[number]["value"];
export type ConsentSource = (typeof CONSENT_SOURCE_OPTIONS)[number]["value"];

export interface DeclarationTicks {
  controller: boolean;
  own_list: boolean;
  consent_calls: boolean;
  consent_texts: boolean;
  consent_records: boolean;
  opt_outs: boolean;
  no_special_category: boolean;
  disclosure: boolean;
  accuracy: boolean;
}

export interface DeclarationForm {
  clientLegalName: string;
  companyNumber: string;
  registeredAddress: string;
  campaignName: string;
  campaignPurpose: string;
  plannedStartDate: string;
  channels: string;
  contactCount: number | null;
  listCompiledOn: string;
  consentSource: string;
  consentCollectedFrom: string;
  consentCollectedTo: string;
  consentWording: string;
  ticks: DeclarationTicks;
  signerFullName: string;
  signerJobTitle: string;
  signerWorkEmail: string;
  typedSignature: string;
  authorised: boolean;
  fileName: string;
}

export interface CapturedDeclarationMeta {
  signedAt: string;
  signerIp: string;
  version: string;
  userId: string;
  contactListSha256: string;
  fileName: string;
  byteSize: number;
}

export interface SignedDeclaration extends Omit<DeclarationForm, "channels" | "consentSource" | "contactCount">, CapturedDeclarationMeta {
  campaignId: string;
  declarationId: string;
  contactListId: string;
  channels: DeclarationChannel;
  consentSource: ConsentSource;
  contactCount: number;
  renderedDeclarations: Array<{ key: DeclarationKey; text: string }>;
  authorisationWording: string;
  pdfBucket: string;
  pdfPath: string;
}

export interface ManagedDeclarationRecord {
  campaignId: string;
  userId: string;
  version: string;
  contactListSha256: string;
  invalidatedAt: string | null;
  signedAt: string | null;
  /** Client legal name on the signed declaration. The per-contact sender must match it. */
  clientLegalName: string;
}

export interface ManagedDeclarationEvidence {
  currentListSha256: string | null;
  currentListId: string | null;
  record: ManagedDeclarationRecord | null;
}

export type ManagedDeclarationBlock =
  | { allowed: true }
  | { allowed: false; message: string };

const CHANNELS = new Set<string>(CHANNEL_OPTIONS.map((item) => item.value));
const SOURCES = new Set<string>(CONSENT_SOURCE_OPTIONS.map((item) => item.value));

export function blankTicks(): DeclarationTicks {
  return {
    controller: false,
    own_list: false,
    consent_calls: false,
    consent_texts: false,
    consent_records: false,
    opt_outs: false,
    no_special_category: false,
    disclosure: false,
    accuracy: false,
  };
}

export function clientDisplayName(value: string): string {
  return value.trim().replace(/\s+/g, " ");
}

export function renderDeclarationText(template: string, clientLegalName: string): string {
  const name = clientDisplayName(clientLegalName);
  return template.split("[Client name]").join(name).split("[Client]").join(name);
}

export function declarationSentences(clientLegalName: string): Array<{ key: DeclarationKey; text: string }> {
  return DECLARATION_TEMPLATES.map((item) => ({
    key: item.key,
    text: renderDeclarationText(item.text, clientLegalName),
  }));
}

export function authorisationStatement(clientLegalName: string): string {
  return renderDeclarationText(AUTHORISATION_TEMPLATE, clientLegalName);
}

export function channelLabel(value: string): string {
  return CHANNEL_OPTIONS.find((item) => item.value === value)?.label ?? value;
}

export function consentSourceLabel(value: string): string {
  return CONSENT_SOURCE_OPTIONS.find((item) => item.value === value)?.label ?? value;
}

export function normalizeListHash(value: string | null | undefined): string {
  return (value ?? "").trim().toLowerCase();
}

export function isSha256Hex(value: string | null | undefined): boolean {
  return /^[a-f0-9]{64}$/.test(normalizeListHash(value));
}

export async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const copy = new Uint8Array(bytes);
  const digest = await crypto.subtle.digest("SHA-256", copy);
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

export function bytesToBase64(bytes: Uint8Array): string {
  if (typeof Buffer !== "undefined") return Buffer.from(bytes).toString("base64");
  let binary = "";
  const chunk = 0x8000;
  for (let index = 0; index < bytes.length; index += chunk) {
    binary += String.fromCharCode(...bytes.subarray(index, index + chunk));
  }
  return btoa(binary);
}

export function base64ToBytes(value: string): Uint8Array | null {
  const trimmed = value.trim();
  if (!trimmed || trimmed.length % 4 !== 0 || !/^[A-Za-z0-9+/]+={0,2}$/.test(trimmed)) return null;
  if (typeof Buffer !== "undefined") return new Uint8Array(Buffer.from(trimmed, "base64"));
  const binary = atob(trimmed);
  const out = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) out[index] = binary.charCodeAt(index);
  return out;
}

export function clientIpFromHeaders(headers: { get(name: string): string | null }): string | null {
  const forwarded = headers.get("x-forwarded-for")?.split(",")[0] ?? "";
  const candidates = [headers.get("cf-connecting-ip"), headers.get("x-real-ip"), forwarded];
  for (const candidate of candidates) {
    const ip = (candidate ?? "").trim();
    if (isPlausibleIp(ip)) return ip;
  }
  return null;
}

function isPlausibleIp(value: string): boolean {
  if (!value || value.length > 64 || /^unknown$/i.test(value)) return false;
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(value)) {
    return value.split(".").every((part) => Number(part) <= 255);
  }
  return value.includes(":") && /^[0-9a-fA-F:.]+$/.test(value);
}

function isIsoDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

function isWorkEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) && value.length <= 320;
}

export function declarationFieldErrors(form: DeclarationForm): string[] {
  const errors: string[] = [];
  const required: Array<[string, string]> = [
    ["Client legal name", form.clientLegalName],
    ["Company number", form.companyNumber],
    ["Registered address", form.registeredAddress],
    ["Campaign name", form.campaignName],
    ["Campaign purpose", form.campaignPurpose],
    ["Full name", form.signerFullName],
    ["Job title", form.signerJobTitle],
    ["Typed signature", form.typedSignature],
    ["Contact list file name", form.fileName],
    ["Consent wording", form.consentWording],
  ];
  for (const [label, value] of required) {
    if (!value.trim()) errors.push(`${label} is required.`);
  }
  if (form.clientLegalName.trim().length > 200) errors.push("Client legal name is too long.");
  if (form.consentWording.trim().length > 4000) errors.push("Consent wording is too long.");
  if (form.registeredAddress.trim().length > 1000) errors.push("Registered address is too long.");
  if (!isIsoDate(form.plannedStartDate.trim())) errors.push("Planned start date is required.");
  if (!isIsoDate(form.listCompiledOn.trim())) errors.push("The date the list was compiled is required.");
  if (!isIsoDate(form.consentCollectedFrom.trim()) || !isIsoDate(form.consentCollectedTo.trim())) {
    errors.push("The consent date range is required.");
  } else if (form.consentCollectedFrom.trim() > form.consentCollectedTo.trim()) {
    errors.push("The consent date range is backwards.");
  }
  if (!CHANNELS.has(form.channels)) errors.push("Choose AI voice calls, SMS, or both.");
  if (!SOURCES.has(form.consentSource)) errors.push("Choose how consent was collected.");
  if (form.contactCount == null || !Number.isInteger(form.contactCount) || form.contactCount < 1) {
    errors.push("Number of contacts is required.");
  }
  if (!isWorkEmail(form.signerWorkEmail.trim())) errors.push("A work email is required.");
  DECLARATION_KEYS.forEach((key, index) => {
    if (form.ticks[key] !== true) errors.push(`Declaration ${index + 1} must be ticked.`);
  });
  if (form.authorised !== true) errors.push("The signature authorisation tick is required.");
  return errors;
}

export async function captureDeclarationMetadata(input: {
  userId: string;
  now: Date;
  headers: { get(name: string): string | null };
  fileBytes: Uint8Array;
  fileName: string;
}): Promise<{ ok: true; meta: CapturedDeclarationMeta } | { ok: false; errors: string[] }> {
  const errors: string[] = [];
  const userId = input.userId.trim();
  if (!userId) errors.push("The signed-in user is missing.");
  const signerIp = clientIpFromHeaders(input.headers);
  if (!signerIp) errors.push("The signer IP address could not be read from the request.");
  const fileName = input.fileName.trim();
  if (!fileName) errors.push("The contact list file name is required.");
  if (input.fileBytes.byteLength < 1) errors.push("The contact list file is empty.");
  if (input.fileBytes.byteLength > CONTACT_LIST_MAX_BYTES) errors.push("The contact list file is too large.");
  if (Number.isNaN(input.now.getTime())) errors.push("The signature time is invalid.");
  if (errors.length || !signerIp) return { ok: false, errors };
  const contactListSha256 = await sha256Hex(input.fileBytes);
  return {
    ok: true,
    meta: {
      signedAt: input.now.toISOString(),
      signerIp,
      version: CLIENT_CONSENT_DECLARATION_VERSION,
      userId,
      contactListSha256,
      fileName,
      byteSize: input.fileBytes.byteLength,
    },
  };
}

function textValue(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function boolValue(value: unknown): boolean {
  return value === true;
}

export function formFromRequestBody(body: Record<string, unknown>): DeclarationForm {
  const ticksRaw = body.ticks && typeof body.ticks === "object" && !Array.isArray(body.ticks)
    ? body.ticks as Record<string, unknown>
    : {};
  const ticks = blankTicks();
  for (const key of DECLARATION_KEYS) ticks[key] = boolValue(ticksRaw[key]);
  const count = body.contact_count;
  return {
    clientLegalName: textValue(body.client_legal_name),
    companyNumber: textValue(body.company_number),
    registeredAddress: textValue(body.registered_address),
    campaignName: textValue(body.campaign_name),
    campaignPurpose: textValue(body.campaign_purpose),
    plannedStartDate: textValue(body.planned_start_date),
    channels: textValue(body.channels),
    contactCount: typeof count === "number" && Number.isInteger(count) ? count : null,
    listCompiledOn: textValue(body.list_compiled_on),
    consentSource: textValue(body.consent_source),
    consentCollectedFrom: textValue(body.consent_collected_from),
    consentCollectedTo: textValue(body.consent_collected_to),
    consentWording: textValue(body.consent_wording),
    ticks,
    signerFullName: textValue(body.signer_full_name),
    signerJobTitle: textValue(body.signer_job_title),
    signerWorkEmail: textValue(body.signer_work_email),
    typedSignature: textValue(body.typed_signature),
    authorised: boolValue(body.authorised),
    fileName: textValue(body.file_name),
  };
}

function rowText(row: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return "";
}

export function asManagedDeclaration(row: Record<string, unknown>): ManagedDeclarationRecord {
  return {
    campaignId: rowText(row, "campaignId", "campaign_id"),
    userId: rowText(row, "userId", "user_id"),
    version: rowText(row, "version", "declaration_version"),
    contactListSha256: normalizeListHash(rowText(row, "contactListSha256", "contact_list_sha256")),
    invalidatedAt: rowText(row, "invalidatedAt", "invalidated_at") || null,
    signedAt: rowText(row, "signedAt", "signed_at") || null,
    clientLegalName: rowText(row, "clientLegalName", "client_legal_name"),
  };
}

export function currentContactList(
  rows: Array<Record<string, unknown>>,
  campaignId: string,
): { id: string | null; sha256: string } | null {
  const mine = rows
    .map((row) => ({
      id: rowText(row, "id") || null,
      campaignId: rowText(row, "campaignId", "campaign_id"),
      sha256: normalizeListHash(rowText(row, "sha256", "contact_list_sha256")),
      uploadedAt: rowText(row, "uploadedAt", "uploaded_at"),
    }))
    .filter((row) => row.campaignId === campaignId && isSha256Hex(row.sha256));
  mine.sort((left, right) => right.uploadedAt.localeCompare(left.uploadedAt));
  const latest = mine[0];
  if (!latest) return null;
  return { id: latest.id, sha256: latest.sha256 };
}

export function currentListHash(
  rows: Array<Record<string, unknown>>,
  campaignId: string,
): string | null {
  return currentContactList(rows, campaignId)?.sha256 ?? null;
}

export function chooseDeclaration(
  rows: Array<Record<string, unknown>>,
  campaignId: string,
  accountUserId: string,
  currentHash: string | null,
): ManagedDeclarationRecord | null {
  const mine = rows
    .map(asManagedDeclaration)
    .filter((row) => row.campaignId === campaignId && row.userId === accountUserId);
  const hash = normalizeListHash(currentHash);
  const valid = mine.filter((row) =>
    !row.invalidatedAt &&
    row.version === CLIENT_CONSENT_DECLARATION_VERSION &&
    row.signedAt &&
    isSha256Hex(row.contactListSha256) &&
    row.contactListSha256 === hash
  );
  const pool = valid.length ? valid : mine;
  pool.sort((left, right) => (right.signedAt ?? "").localeCompare(left.signedAt ?? ""));
  return pool[0] ?? null;
}

export function evidenceFromRows(input: {
  accountUserId: string;
  campaignId: string;
  declarations: Array<Record<string, unknown>>;
  lists: Array<Record<string, unknown>>;
}): ManagedDeclarationEvidence {
  const current = currentContactList(input.lists, input.campaignId);
  return {
    currentListSha256: current?.sha256 ?? null,
    currentListId: current?.id ?? null,
    record: chooseDeclaration(input.declarations, input.campaignId, input.accountUserId, current?.sha256 ?? null),
  };
}

/**
 * A new contact list replaces the previous one when the file hash changes.
 * The same bytes do not invalidate the declaration that already covers them.
 */
export function applyListReplacement<T extends {
  campaignId: string;
  contactListSha256: string;
  invalidatedAt: string | null;
}>(
  declarations: T[],
  upload: { campaignId: string; sha256: string; at: string },
): T[] {
  const nextHash = normalizeListHash(upload.sha256);
  return declarations.map((row) => {
    if (row.campaignId !== upload.campaignId || row.invalidatedAt) return row;
    if (normalizeListHash(row.contactListSha256) === nextHash) return row;
    return { ...row, invalidatedAt: upload.at };
  });
}

export function assessManagedCampaignDeclaration(input: {
  accountUserId: string;
  campaignId: string | null | undefined;
  evidence: ManagedDeclarationEvidence | null | undefined;
}): ManagedDeclarationBlock {
  if (!input.campaignId) {
    return {
      allowed: false,
      message: "This managed campaign call is not tied to a campaign, so it was not placed.",
    };
  }
  if (!input.evidence) {
    return {
      allowed: false,
      message: "The signed declaration for this managed campaign could not be checked, so the call was not placed.",
    };
  }
  const hash = normalizeListHash(input.evidence.currentListSha256);
  if (!isSha256Hex(hash)) {
    return {
      allowed: false,
      message: "This managed campaign has no recorded contact list, so no call, retry, or follow-up was placed.",
    };
  }
  const record = input.evidence.record;
  if (!record || record.userId !== input.accountUserId || record.campaignId !== input.campaignId) {
    return {
      allowed: false,
      message: "This managed campaign has no signed client consent declaration for its current contact list, so the call was not placed.",
    };
  }
  if (record.invalidatedAt) {
    return {
      allowed: false,
      message: "The client consent declaration was invalidated when a new contact list was uploaded. Sign it again before any call, retry, or follow-up.",
    };
  }
  if (record.version !== CLIENT_CONSENT_DECLARATION_VERSION || !record.signedAt) {
    return {
      allowed: false,
      message: "The client consent declaration is not the signed v1 (23 Oct 2026) declaration, so the call was not placed.",
    };
  }
  if (normalizeListHash(record.contactListSha256) !== hash) {
    return {
      allowed: false,
      message: "The contact list has changed since the client consent declaration was signed. Sign it again before any call, retry, or follow-up.",
    };
  }
  return { allowed: true };
}

const WIN_ANSI: Record<string, number> = {
  "–": 0x96,
  "—": 0x97,
  "‘": 0x91,
  "’": 0x92,
  "“": 0x93,
  "”": 0x94,
};

function pdfText(value: string): string {
  let out = "";
  for (const char of value) {
    if (char === "\\" || char === "(" || char === ")") {
      out += `\\${char}`;
      continue;
    }
    const code = char.codePointAt(0) ?? 63;
    if (code >= 32 && code <= 126) {
      out += char;
      continue;
    }
    const mapped = WIN_ANSI[char];
    if (mapped != null) {
      out += `\\${mapped.toString(8).padStart(3, "0")}`;
      continue;
    }
    out += "?";
  }
  return out;
}

function wrapLine(value: string, width = 92): string[] {
  const words = value.split(/\s+/).filter((word) => word.length > 0);
  if (!words.length) return [""];
  const lines: string[] = [];
  let current = "";
  for (const word of words) {
    const next = current ? `${current} ${word}` : word;
    if (next.length > width && current) {
      lines.push(current);
      current = word;
    } else {
      current = next;
    }
  }
  if (current) lines.push(current);
  return lines;
}

function declarationLines(declaration: SignedDeclaration): string[] {
  const lines = [
    DECLARATION_TITLE,
    DECLARATION_INTRO,
    "",
    "Campaign details",
    `Client legal name: ${declaration.clientLegalName}`,
    `Company number: ${declaration.companyNumber}`,
    `Registered address: ${declaration.registeredAddress}`,
    `Campaign name: ${declaration.campaignName}`,
    `Purpose: ${declaration.campaignPurpose}`,
    `Planned start date: ${declaration.plannedStartDate}`,
    `Channels: ${channelLabel(declaration.channels)}`,
    `Contact list file name: ${declaration.fileName}`,
    `Number of contacts: ${declaration.contactCount}`,
    `Date the list was compiled: ${declaration.listCompiledOn}`,
    `How consent was collected: ${consentSourceLabel(declaration.consentSource)}`,
    `Consent date range: ${declaration.consentCollectedFrom} to ${declaration.consentCollectedTo}`,
    `Exact consent wording: ${declaration.consentWording}`,
    "",
    "Declarations",
  ];
  declaration.renderedDeclarations.forEach((item, index) => {
    lines.push(`${index + 1}. [x] ${item.text}`);
  });
  lines.push(
    "",
    "Signature",
    `Full name: ${declaration.signerFullName}`,
    `Job title: ${declaration.signerJobTitle}`,
    `Work email: ${declaration.signerWorkEmail}`,
    `Typed signature: ${declaration.typedSignature}`,
    `[x] ${declaration.authorisationWording}`,
    "",
    "Captured automatically",
    `Date and time (UTC): ${declaration.signedAt}`,
    `IP address: ${declaration.signerIp}`,
    `Declaration version: ${declaration.version} (${CLIENT_CONSENT_DECLARATION_LABEL})`,
    `User id: ${declaration.userId}`,
    `Contact list file hash (SHA-256): ${declaration.contactListSha256}`,
    `Declaration id: ${declaration.declarationId}`,
    `Campaign id: ${declaration.campaignId}`,
  );
  return lines.flatMap((line) => wrapLine(line));
}

export function renderDeclarationPdf(declaration: SignedDeclaration): Uint8Array {
  const wrapped = declarationLines(declaration);
  const linesPerPage = 46;
  const pages: string[][] = [];
  for (let index = 0; index < wrapped.length; index += linesPerPage) {
    pages.push(wrapped.slice(index, index + linesPerPage));
  }
  if (!pages.length) pages.push([""]);
  const streams = pages.map((page) => {
    const commands = ["BT", "/F1 9 Tf", "48 760 Td", "14 TL"];
    page.forEach((line, index) => {
      if (index > 0) commands.push("T*");
      commands.push(`(${pdfText(line)}) Tj`);
    });
    commands.push("ET");
    return commands.join("\n");
  });
  return buildPdf(streams);
}

function buildPdf(streams: string[]): Uint8Array {
  const fontId = 3;
  let next = 4;
  const pageIds: number[] = [];
  const contentIds: number[] = [];
  for (let index = 0; index < streams.length; index += 1) {
    pageIds.push(next);
    next += 1;
    contentIds.push(next);
    next += 1;
  }
  const objectCount = next - 1;
  const bodies = new Array<string>(objectCount + 1);
  bodies[1] = `<< /Type /Catalog /Pages 2 0 R >>`;
  bodies[2] = `<< /Type /Pages /Count ${pageIds.length} /Kids [${pageIds.map((id) => `${id} 0 R`).join(" ")}] >>`;
  bodies[3] = `<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>`;
  streams.forEach((stream, index) => {
    const pageId = pageIds[index];
    const contentId = contentIds[index];
    bodies[pageId] = `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Contents ${contentId} 0 R /Resources << /Font << /F1 ${fontId} 0 R >> >> >>`;
    bodies[contentId] = `<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`;
  });

  const encoder = new TextEncoder();
  const chunks: Uint8Array[] = [];
  let offset = 0;
  const push = (value: string) => {
    const bytes = encoder.encode(value);
    chunks.push(bytes);
    offset += bytes.length;
  };
  const offsets = new Array<number>(objectCount + 1).fill(0);
  push("%PDF-1.4\n");
  for (let id = 1; id <= objectCount; id += 1) {
    offsets[id] = offset;
    push(`${id} 0 obj\n${bodies[id]}\nendobj\n`);
  }
  const xref = offset;
  push(`xref\n0 ${objectCount + 1}\n`);
  push("0000000000 65535 f \n");
  for (let id = 1; id <= objectCount; id += 1) {
    push(`${String(offsets[id]).padStart(10, "0")} 00000 n \n`);
  }
  push(`trailer\n<< /Size ${objectCount + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF\n`);
  const total = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const pdf = new Uint8Array(total);
  let cursor = 0;
  for (const chunk of chunks) {
    pdf.set(chunk, cursor);
    cursor += chunk.length;
  }
  return pdf;
}

export interface DeclarationWritePort {
  insertContactList(row: Record<string, unknown>): Promise<{ error: string | null }>;
  uploadPdf(path: string, bytes: Uint8Array): Promise<{ error: string | null }>;
  insertDeclaration(row: Record<string, unknown>): Promise<{ error: string | null }>;
  removePdf(path: string): Promise<void>;
}

export interface PreparedDeclaration {
  declaration: SignedDeclaration;
  pdf: Uint8Array;
  listRow: Record<string, unknown>;
  declarationRow: Record<string, unknown>;
}

export async function prepareCampaignDeclaration(input: {
  campaignId: string;
  userId: string;
  now: Date;
  headers: { get(name: string): string | null };
  body: Record<string, unknown>;
}): Promise<{ ok: true; prepared: PreparedDeclaration } | { ok: false; errors: string[] }> {
  const campaignId = input.campaignId.trim();
  if (!campaignId) return { ok: false, errors: ["Campaign is required."] };
  const form = formFromRequestBody({ ...input.body, file_name: input.body.file_name });
  const fileBytes = base64ToBytes(textValue(input.body.file_base64));
  if (!fileBytes) return { ok: false, errors: ["The contact list file could not be read."] };
  const fieldErrors = declarationFieldErrors(form);
  if (fieldErrors.length) return { ok: false, errors: fieldErrors };
  const captured = await captureDeclarationMetadata({
    userId: input.userId,
    now: input.now,
    headers: input.headers,
    fileBytes,
    fileName: form.fileName,
  });
  if (captured.ok === false) return { ok: false, errors: captured.errors };

  const declarationId = crypto.randomUUID();
  const contactListId = crypto.randomUUID();
  const pdfPath = `${captured.meta.userId}/${campaignId}/${declarationId}.pdf`;
  const renderedDeclarations = declarationSentences(form.clientLegalName);
  const declaration: SignedDeclaration = {
    ...form,
    channels: form.channels as DeclarationChannel,
    consentSource: form.consentSource as ConsentSource,
    contactCount: form.contactCount as number,
    ...captured.meta,
    version: CLIENT_CONSENT_DECLARATION_VERSION,
    userId: captured.meta.userId,
    signedAt: captured.meta.signedAt,
    signerIp: captured.meta.signerIp,
    contactListSha256: captured.meta.contactListSha256,
    campaignId,
    declarationId,
    contactListId,
    renderedDeclarations,
    authorisationWording: authorisationStatement(form.clientLegalName),
    pdfBucket: CLIENT_CONSENT_PDF_BUCKET,
    pdfPath,
  };
  const pdf = renderDeclarationPdf(declaration);
  if (pdf.byteLength < 20 || !pdfStartsWithPdf(pdf)) {
    return { ok: false, errors: ["The declaration PDF could not be generated."] };
  }
  return {
    ok: true,
    prepared: {
      declaration,
      pdf,
      listRow: {
        id: contactListId,
        campaign_id: campaignId,
        user_id: captured.meta.userId,
        file_name: captured.meta.fileName,
        sha256: captured.meta.contactListSha256,
        byte_size: captured.meta.byteSize,
        uploaded_at: captured.meta.signedAt,
        uploaded_by: captured.meta.userId,
      },
      declarationRow: declarationInsertRow(declaration),
    },
  };
}

function pdfStartsWithPdf(bytes: Uint8Array): boolean {
  return bytes.length >= 5 &&
    bytes[0] === 0x25 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x44 &&
    bytes[3] === 0x46;
}

export function declarationInsertRow(declaration: SignedDeclaration): Record<string, unknown> {
  return {
    id: declaration.declarationId,
    user_id: declaration.userId,
    campaign_id: declaration.campaignId,
    contact_list_id: declaration.contactListId,
    declaration_version: declaration.version,
    client_legal_name: declaration.clientLegalName.trim(),
    company_number: declaration.companyNumber.trim(),
    registered_address: declaration.registeredAddress.trim(),
    campaign_name: declaration.campaignName.trim(),
    campaign_purpose: declaration.campaignPurpose.trim(),
    planned_start_date: declaration.plannedStartDate.trim(),
    channels: declaration.channels,
    contact_list_file_name: declaration.fileName.trim(),
    contact_count: declaration.contactCount,
    list_compiled_on: declaration.listCompiledOn.trim(),
    consent_source: declaration.consentSource,
    consent_collected_from: declaration.consentCollectedFrom.trim(),
    consent_collected_to: declaration.consentCollectedTo.trim(),
    consent_wording: declaration.consentWording.trim(),
    decl_controller: true,
    decl_own_list: true,
    decl_consent_calls: true,
    decl_consent_texts: true,
    decl_consent_records: true,
    decl_opt_outs: true,
    decl_no_special_category: true,
    decl_disclosure: true,
    decl_accuracy: true,
    signer_full_name: declaration.signerFullName.trim(),
    signer_job_title: declaration.signerJobTitle.trim(),
    signer_work_email: declaration.signerWorkEmail.trim(),
    typed_signature: declaration.typedSignature.trim(),
    authorised_to_sign: true,
    authorisation_wording: declaration.authorisationWording,
    signed_at: declaration.signedAt,
    signer_ip: declaration.signerIp,
    contact_list_sha256: declaration.contactListSha256,
    pdf_bucket: declaration.pdfBucket,
    pdf_path: declaration.pdfPath,
    retention_policy: CONSENT_RETENTION_POLICY,
    hubspot_noted_at: null,
    invalidated_at: null,
  };
}

export async function storePreparedDeclaration(
  port: DeclarationWritePort,
  prepared: PreparedDeclaration,
): Promise<{ ok: true; id: string; pdfPath: string; sha256: string } | { ok: false; errors: string[] }> {
  const uploaded = await port.uploadPdf(prepared.declaration.pdfPath, prepared.pdf);
  if (uploaded.error) return { ok: false, errors: ["The declaration PDF could not be stored."] };
  // The list row is inserted before the declaration so the foreign key exists.
  // A different hash invalidates older declarations. If the declaration insert
  // then fails, the new list is already current and dialling stays closed.
  const list = await port.insertContactList(prepared.listRow);
  if (list.error) {
    await port.removePdf(prepared.declaration.pdfPath);
    return { ok: false, errors: ["The contact list hash could not be recorded, so nothing was signed."] };
  }
  const declaration = await port.insertDeclaration(prepared.declarationRow);
  if (declaration.error) {
    await port.removePdf(prepared.declaration.pdfPath);
    return {
      ok: false,
      errors: ["The declaration could not be stored. The new contact list was recorded, so sign again before any call."],
    };
  }
  return {
    ok: true,
    id: prepared.declaration.declarationId,
    pdfPath: prepared.declaration.pdfPath,
    sha256: prepared.declaration.contactListSha256,
  };
}

export interface CampaignOwner {
  id: string;
  userId: string;
}

export async function signCampaignDeclaration(deps: {
  loadCampaign: (campaignId: string) => Promise<CampaignOwner | null | "error">;
  port: DeclarationWritePort;
  userId: string;
  now: Date;
  headers: { get(name: string): string | null };
  body: Record<string, unknown>;
}): Promise<
  | { ok: true; id: string; pdfPath: string; sha256: string; signedAt: string }
  | { ok: false; status: number; errors: string[] }
> {
  const campaignId = textValue(deps.body.campaign_id);
  const campaign = await deps.loadCampaign(campaignId);
  if (campaign === "error") return { ok: false, status: 503, errors: ["The campaign could not be checked."] };
  if (!campaign || campaign.userId !== deps.userId) {
    return { ok: false, status: 403, errors: ["This campaign is not on your account."] };
  }
  const prepared = await prepareCampaignDeclaration({
    campaignId,
    userId: deps.userId,
    now: deps.now,
    headers: deps.headers,
    body: deps.body,
  });
  if (prepared.ok === false) return { ok: false, status: 400, errors: prepared.errors };
  const stored = await storePreparedDeclaration(deps.port, prepared.prepared);
  if (stored.ok === false) return { ok: false, status: 503, errors: stored.errors };
  return { ...stored, signedAt: prepared.prepared.declaration.signedAt };
}

export async function registerCampaignContactList(deps: {
  loadCampaign: (campaignId: string) => Promise<CampaignOwner | null | "error">;
  insertContactList: (row: Record<string, unknown>) => Promise<{ error: string | null }>;
  userId: string;
  now: Date;
  body: Record<string, unknown>;
}): Promise<{ ok: true; sha256: string; id: string } | { ok: false; status: number; errors: string[] }> {
  const campaignId = textValue(deps.body.campaign_id);
  const campaign = await deps.loadCampaign(campaignId);
  if (campaign === "error") return { ok: false, status: 503, errors: ["The campaign could not be checked."] };
  if (!campaign || campaign.userId !== deps.userId) {
    return { ok: false, status: 403, errors: ["This campaign is not on your account."] };
  }
  const fileName = textValue(deps.body.file_name);
  const fileBytes = base64ToBytes(textValue(deps.body.file_base64));
  if (!fileName || !fileBytes) return { ok: false, status: 400, errors: ["The contact list file could not be read."] };
  if (fileBytes.byteLength < 1 || fileBytes.byteLength > CONTACT_LIST_MAX_BYTES) {
    return { ok: false, status: 400, errors: ["The contact list file is empty or too large."] };
  }
  const sha256 = await sha256Hex(fileBytes);
  const id = crypto.randomUUID();
  const inserted = await deps.insertContactList({
    id,
    campaign_id: campaignId,
    user_id: deps.userId,
    file_name: fileName,
    sha256,
    byte_size: fileBytes.byteLength,
    uploaded_at: deps.now.toISOString(),
    uploaded_by: deps.userId,
  });
  if (inserted.error) return { ok: false, status: 503, errors: ["The contact list hash could not be recorded."] };
  return { ok: true, sha256, id };
}
